/**
 * Iris studio design primitives.
 *
 * The studio has its own visual language — warm paper, hairline rules, mono
 * eyebrows, near-black actions — expressed through the `--iris-*` tokens
 * scoped to `.iris-studio` in index.css. These are the shared building blocks
 * so every tab renders from the same vocabulary instead of ad-hoc utilities.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Check, ChevronRight, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Type ────────────────────────────────────────────────────────────────── */

/** Uppercase mono micro-label. The studio's only heading device above a card. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'font-mono text-[10px] uppercase leading-none tracking-[0.12em] text-[var(--iris-muted)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Monospaced inline value — slugs, ids, counts. */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('font-mono text-[10.5px] text-[var(--iris-muted)]', className)}>
      {children}
    </span>
  );
}

/* ─── Containers ──────────────────────────────────────────────────────────── */

/** The standard studio card: white surface, hairline border, 12px radius. */
export function IrisCard({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-[var(--iris-rule)] bg-[var(--iris-surface)]',
        padded && 'px-[18px] py-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A card that leads with an eyebrow — the most common studio panel. */
export function IrisPanel({
  title,
  action,
  children,
  className,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <IrisCard className={className}>
      <div className="flex items-start justify-between gap-3">
        <Eyebrow>{title}</Eyebrow>
        {action}
      </div>
      <div className="mt-2.5">{children}</div>
    </IrisCard>
  );
}

/* ─── Chips & badges ──────────────────────────────────────────────────────── */

/**
 * Pill chip. `tone` picks one of the flat status washes.
 *
 * The chip is an inline-flex box, so `truncate` on the chip itself does
 * nothing — `text-overflow` needs a block. Cap the width here and put
 * `truncate` on a child, and pass `title` so the full value stays reachable.
 */
export function Chip({
  children,
  tone = 'neutral',
  className,
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'soft' | 'warn' | 'note' | 'info' | 'good';
  className?: string;
  /** Hover text — worth setting whenever the chip can be truncated. */
  title?: string;
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-[var(--iris-line)] text-[var(--iris-body)]',
    soft: 'bg-[var(--iris-soft)] text-[var(--iris-body)] border border-[var(--iris-hairline)]',
    warn: 'bg-[var(--iris-warn-bg)] text-[var(--iris-warn-fg)]',
    note: 'bg-[var(--iris-note-bg)] text-[var(--iris-note-fg)]',
    info: 'bg-[var(--iris-info-bg)] text-[var(--iris-info-fg)]',
    good: 'bg-[var(--iris-good-bg)] text-[var(--iris-good-fg)]',
  };
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10.5px] font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Mono variant of `Chip` — for counts and machine names in card headers. */
export function MonoChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full bg-[var(--iris-line)] px-2.5 py-1 font-mono text-[10.5px] text-[var(--iris-body)]',
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A flat coloured note strip — warnings, hints, callouts under a control. */
export function IrisNote({
  tone = 'note',
  children,
  className,
}: {
  tone?: 'warn' | 'note' | 'info' | 'good';
  children: ReactNode;
  className?: string;
}) {
  const tones: Record<string, string> = {
    warn: 'bg-[var(--iris-warn-bg)] text-[var(--iris-warn-fg)]',
    note: 'bg-[var(--iris-note-bg)] text-[var(--iris-note-fg)]',
    info: 'bg-[var(--iris-info-bg)] text-[var(--iris-info-fg)]',
    good: 'bg-[var(--iris-good-bg)] text-[var(--iris-good-fg)]',
  };
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md px-2.5 py-2 text-xs leading-relaxed',
        tones[tone],
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ─── Actions ─────────────────────────────────────────────────────────────── */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

/** Solid near-black action — one per surface, the thing you actually do. */
export const IrisButton = forwardRef<HTMLButtonElement, ButtonProps>(function IrisButton(
  { className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg bg-[var(--iris-ink)] px-3 py-2 text-[13px] font-medium text-[var(--iris-on-ink)]',
        'hover:bg-[var(--iris-ink-hover)] active:scale-[0.98]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--iris-paper)]',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      {children}
    </button>
  );
});

/** Outlined secondary action on the paper surface. */
export const IrisGhostButton = forwardRef<HTMLButtonElement, ButtonProps>(function IrisGhostButton(
  { className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg border border-[var(--iris-rule-strong)] bg-[var(--iris-surface)] px-3 py-2 text-[13px] text-[var(--iris-ink)]',
        'hover:border-[var(--iris-ink)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--iris-paper)]',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      {children}
    </button>
  );
});

/** Dashed "add another" affordance that opens a composer. */
export const IrisAddButton = forwardRef<HTMLButtonElement, ButtonProps>(function IrisAddButton(
  { className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg border border-dashed border-[var(--iris-rule-strong)] bg-[var(--iris-surface)] px-3 py-2 text-[13px] text-[var(--iris-body)]',
        'hover:border-[var(--iris-ink)] hover:text-[var(--iris-ink)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--iris-paper)]',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      <Plus className="size-3.5" />
      {children}
    </button>
  );
});

/** Underlined text action — tertiary, sits inline with prose. */
export const IrisLinkButton = forwardRef<HTMLButtonElement, ButtonProps>(function IrisLinkButton(
  { className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        'text-xs text-[var(--iris-muted)] underline underline-offset-2 hover:text-[var(--iris-ink)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-1',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      {children}
    </button>
  );
});

/** Small square icon action — row-level delete, reorder, dismiss. */
export const IrisIconButton = forwardRef<HTMLButtonElement, ButtonProps>(function IrisIconButton(
  { className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        'grid size-6 shrink-0 place-items-center rounded-md text-[var(--iris-muted)]',
        'hover:bg-[var(--iris-line)] hover:text-[var(--iris-ink)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-1',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      {children}
    </button>
  );
});

/* ─── Fields ──────────────────────────────────────────────────────────────── */

/** Shared field chrome for the studio's inputs, selects and textareas. */
export const irisField = cn(
  'w-full rounded-md border border-[var(--iris-rule)] bg-[var(--iris-surface)] px-2.5 py-1.5',
  'text-[13px] text-[var(--iris-ink)] placeholder:text-[var(--iris-ghost)]',
  'focus:border-[var(--iris-ink)] focus:outline-none focus:ring-[3px] focus:ring-[var(--iris-ink)]/10',
  'disabled:opacity-50',
);

/** Dashed variant — the empty composer row waiting for a first value. */
export const irisDraftField = cn(
  irisField,
  'border-dashed border-[var(--iris-rule-strong)] focus:border-solid',
);

/* ─── Status marks ────────────────────────────────────────────────────────── */

/**
 * Round checklist mark. `done` fills near-black, `partial` outlines, and the
 * empty state is a hairline ring — the studio never uses colour for progress.
 */
export function IrisMark({ state }: { state: 'done' | 'partial' | 'todo' }) {
  if (state === 'done') {
    return (
      <span className="grid size-[18px] shrink-0 place-items-center rounded-full bg-[var(--iris-ink)] text-[var(--iris-on-ink)]">
        <Check className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (state === 'partial') {
    return (
      <span className="grid size-[18px] shrink-0 place-items-center rounded-full border border-[var(--iris-ink)] text-[var(--iris-ink)]">
        <Minus className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className="grid size-[18px] shrink-0 place-items-center rounded-full border border-[var(--iris-rule-strong)]" />
  );
}

/* ─── Toggle ──────────────────────────────────────────────────────────────── */

/** Pill switch in the studio's ink/line palette. */
export function IrisToggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-[var(--iris-ink)]' : 'bg-[var(--iris-line)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--iris-surface)]',
        'disabled:pointer-events-none disabled:opacity-40',
      )}
    >
      <span
        className={cn(
          'size-3.5 rounded-full bg-[var(--iris-surface)] shadow-sm transition-transform',
          checked ? 'translate-x-[16px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}

/* ─── Numbered step card ──────────────────────────────────────────────────── */

/**
 * The Train tab's spine: a numbered, collapsible step whose closed state still
 * reports its summary and count, so the whole setup reads as a checklist.
 */
export function IrisStep({
  n,
  title,
  summary,
  chip,
  open,
  onToggle,
  children,
}: {
  n: string;
  title: string;
  summary: string;
  chip: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border bg-[var(--iris-surface)]',
        open ? 'border-[var(--iris-rule-strong)]' : 'border-[var(--iris-rule)]',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3.5 px-[18px] py-3.5 text-left hover:bg-[var(--iris-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--iris-ink)]"
      >
        <span className="w-4 shrink-0 font-mono text-[11px] text-[var(--iris-muted)]">{n}</span>
        <span className="w-[118px] shrink-0 text-[15px] font-medium text-[var(--iris-ink)]">
          {title}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] text-[var(--iris-muted)]">
          {summary}
        </span>
        <MonoChip>{chip}</MonoChip>
        <ChevronRight
          className={cn(
            'size-4 shrink-0 text-[var(--iris-muted)] transition-transform',
            open && 'rotate-90',
          )}
        />
      </button>
      {open ? <div className="border-t border-[var(--iris-hairline)]">{children}</div> : null}
    </div>
  );
}

/**
 * Disclosure row used inside steps — knowledge facts, answers, prompt blocks.
 * Same anatomy as `IrisStep` one level down: chevron, title, preview, meta.
 */
export function IrisDisclosure({
  title,
  preview,
  meta,
  open,
  onToggle,
  trailing,
  children,
}: {
  title: ReactNode;
  preview?: ReactNode;
  meta?: ReactNode;
  open: boolean;
  onToggle: () => void;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--iris-rule)] bg-[var(--iris-surface)]">
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-1"
        >
          <ChevronRight
            className={cn(
              'size-3.5 shrink-0 text-[var(--iris-muted)] transition-transform',
              open && 'rotate-90',
            )}
          />
          <span className="shrink-0 text-[13.5px] font-medium text-[var(--iris-ink)]">{title}</span>
          {preview ? (
            <span className="min-w-0 flex-1 truncate text-xs text-[var(--iris-muted)]">
              {preview}
            </span>
          ) : null}
        </button>
        {meta}
        {trailing}
      </div>
      {open ? <div className="px-3 pb-3">{children}</div> : null}
    </div>
  );
}

/* ─── Empty state ─────────────────────────────────────────────────────────── */

export function IrisEmpty({
  title,
  children,
  className,
}: {
  title: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-dashed border-[var(--iris-rule-strong)] px-6 py-7 text-center',
        className,
      )}
    >
      <p className="text-sm font-medium text-[var(--iris-ink)]">{title}</p>
      {children ? <p className="mt-1.5 text-xs text-[var(--iris-muted)]">{children}</p> : null}
    </div>
  );
}

/* ─── Filter pills ────────────────────────────────────────────────────────── */

/** Segmented filter row used by Inbox and Reviews. */
export function IrisFilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--iris-ink)] focus-visible:ring-offset-1',
        active
          ? 'border-[var(--iris-ink)] bg-[var(--iris-ink)] text-[var(--iris-on-ink)]'
          : 'border-[var(--iris-rule)] bg-[var(--iris-surface)] text-[var(--iris-body)] hover:border-[var(--iris-ink)]',
      )}
    >
      {children}
    </button>
  );
}
